(() => {
  const SELECTORS = {
    root: '[data-dealer-locator]',
    controls: '[data-dealer-controls]',
    search: '[data-dealer-search]',
    country: '[data-dealer-country]',
    location: '[data-dealer-location]',
    count: '[data-dealer-count]',
    results: '[data-dealer-results]',
    empty: '[data-dealer-empty]',
    mapFrame: '[data-dealer-map-frame]',
    mapTitle: '[data-dealer-map-title]',
    resetMap: '[data-dealer-reset-map]',
    live: '[data-dealer-live]',
    view: '[data-dealer-view]',
  };

  class MaybachDealerLocator {
    constructor(root) {
      this.root = root;
      this.controls = root.querySelector(SELECTORS.controls);
      this.search = root.querySelector(SELECTORS.search);
      this.country = root.querySelector(SELECTORS.country);
      this.locationButton = root.querySelector(SELECTORS.location);
      this.count = root.querySelector(SELECTORS.count);
      this.results = root.querySelector(SELECTORS.results);
      this.empty = root.querySelector(SELECTORS.empty);
      this.mapFrame = root.querySelector(SELECTORS.mapFrame);
      this.mapTitle = root.querySelector(SELECTORS.mapTitle);
      this.resetMapButton = root.querySelector(SELECTORS.resetMap);
      this.live = root.querySelector(SELECTORS.live);
      this.viewButtons = Array.from(root.querySelectorAll(SELECTORS.view));
      this.dealers = Array.isArray(window.MaybachDealerData) ? window.MaybachDealerData : [];
      this.selectedDealerId = null;
      this.userLocation = null;
      this.locale = document.documentElement.lang || 'en';
      this.collator = new Intl.Collator(this.locale, { sensitivity: 'base', numeric: true });
      this.regionNames = this.createRegionNames();

      if (!this.controls || !this.search || !this.country || !this.results || !this.mapFrame) return;

      this.populateCountries();
      this.bindEvents();
      this.render();
    }

    createRegionNames() {
      if (typeof Intl.DisplayNames !== 'function') return null;
      try {
        return new Intl.DisplayNames([this.locale], { type: 'region' });
      } catch (error) {
        return null;
      }
    }

    countryName(dealer) {
      if (!dealer.countryCode || !this.regionNames) return dealer.country;
      return this.regionNames.of(dealer.countryCode) || dealer.country;
    }

    populateCountries() {
      const countries = new Map();
      this.dealers.forEach((dealer) => {
        if (dealer.countryCode) countries.set(dealer.countryCode, this.countryName(dealer));
      });

      Array.from(countries.entries())
        .sort((a, b) => this.collator.compare(a[1], b[1]))
        .forEach(([code, name]) => {
          const option = document.createElement('option');
          option.value = code;
          option.textContent = name;
          this.country.append(option);
        });
    }

    bindEvents() {
      this.search.addEventListener('input', () => this.handleFilterChange());
      this.country.addEventListener('change', () => this.handleFilterChange());
      this.controls.addEventListener('submit', (event) => event.preventDefault());
      this.controls.addEventListener('reset', () => {
        window.requestAnimationFrame(() => {
          this.userLocation = null;
          this.render();
          this.resetMap();
        });
      });
      this.locationButton.addEventListener('click', () => this.findNearestDealers());
      this.results.addEventListener('click', (event) => this.handleResultClick(event));
      this.resetMapButton.addEventListener('click', () => this.resetMap());
      this.viewButtons.forEach((button) => {
        button.addEventListener('click', () => this.setMobileView(button.dataset.dealerView));
      });
    }

    handleFilterChange() {
      if (this.selectedDealerId) {
        this.resetMap();
      } else {
        this.render();
      }
    }

    normalise(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase(this.locale)
        .trim();
    }

    filteredDealers() {
      const query = this.normalise(this.search.value);
      const countryCode = this.country.value;
      const dealers = this.dealers
        .filter((dealer) => !countryCode || dealer.countryCode === countryCode)
        .filter((dealer) => {
          if (!query) return true;
          const haystack = this.normalise(
            [
              dealer.name,
              dealer.address,
              dealer.city,
              dealer.postcode,
              dealer.country,
              this.countryName(dealer),
              dealer.countryCode,
              dealer.telephone,
              dealer.email,
              dealer.website,
              ...(Array.isArray(dealer.searchPlaces) ? dealer.searchPlaces : []),
            ].join(' ')
          );
          return haystack.includes(query);
        })
        .map((dealer) => ({ ...dealer }));

      if (this.userLocation) {
        dealers.forEach((dealer) => {
          dealer.distance = this.distanceInKilometres(this.userLocation, dealer);
        });
        dealers.sort((a, b) => a.distance - b.distance);
      }

      return dealers;
    }

    render() {
      const dealers = this.filteredDealers();
      const label = dealers.length === 1 ? this.root.dataset.dealerSingular : this.root.dataset.dealerPlural;
      this.count.textContent = `${dealers.length} ${label}`;
      this.live.textContent = this.count.textContent;
      this.empty.hidden = dealers.length !== 0;
      this.results.replaceChildren();

      const fragment = document.createDocumentFragment();
      dealers.forEach((dealer) => fragment.append(this.createDealerCard(dealer)));
      this.results.append(fragment);
    }

    createDealerCard(dealer) {
      const article = document.createElement('article');
      article.className = 'maybach-dealer-locator__dealer';
      article.dataset.dealerId = dealer.id;
      article.setAttribute('role', 'listitem');
      if (dealer.id === this.selectedDealerId) article.classList.add('is-selected');

      const heading = document.createElement('h2');
      heading.textContent = dealer.name;
      article.append(heading);

      const meta = document.createElement('p');
      meta.className = 'maybach-dealer-locator__dealer-meta';
      if (dealer.postcode) {
        const postcode = document.createElement('span');
        postcode.textContent = dealer.postcode;
        meta.append(postcode);
      }
      const country = document.createElement('span');
      country.textContent = this.countryName(dealer);
      meta.append(country);
      if (Number.isFinite(dealer.distance)) {
        const distance = document.createElement('span');
        distance.className = 'maybach-dealer-locator__dealer-distance';
        distance.textContent = `${this.formatDistance(dealer.distance)} ${this.root.dataset.distanceLabel}`;
        meta.append(distance);
      }
      article.append(meta);

      const addressParts = [dealer.address, dealer.city, dealer.postcode, this.countryName(dealer)].filter(Boolean);
      if (addressParts.length) {
        const address = document.createElement('address');
        address.className = 'maybach-dealer-locator__dealer-address';
        address.textContent = addressParts.join(', ');
        article.append(address);
      }

      const contact = document.createElement('ul');
      contact.className = 'maybach-dealer-locator__dealer-contact';

      if (dealer.telephone) {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `tel:${dealer.telephone.replace(/[^+\d]/g, '')}`;
        link.textContent = `Tel: ${dealer.telephone}`;
        link.setAttribute('aria-label', `Call ${dealer.name} on ${dealer.telephone}`);
        item.append(link);
        contact.append(item);
      }

      if (dealer.email) {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `mailto:${dealer.email}`;
        link.textContent = `Email: ${dealer.email}`;
        item.append(link);
        contact.append(item);
      }

      if (dealer.website) {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = dealer.website;
        link.target = '_blank';
        link.rel = 'noopener';
        const websiteLabel = this.root.dataset.websiteLabel || 'Website';
        const websiteText = dealer.website.replace(/^https?:\/\//, '').replace(/\/$/, '');
        link.textContent = `${websiteLabel}: ${websiteText}`;
        link.setAttribute('aria-label', `${dealer.name} website`);
        item.append(link);
        contact.append(item);
      }

      if (contact.children.length) article.append(contact);

      const actions = document.createElement('div');
      actions.className = 'maybach-dealer-locator__dealer-actions';

      const mapButton = document.createElement('button');
      mapButton.type = 'button';
      mapButton.dataset.showDealer = dealer.id;
      mapButton.textContent = this.root.dataset.showOnMap;
      actions.append(mapButton);

      const directions = document.createElement('a');
      directions.href = `https://www.google.com/maps/dir/?api=1&destination=${dealer.latitude},${dealer.longitude}`;
      directions.target = '_blank';
      directions.rel = 'noopener';
      directions.textContent = this.root.dataset.directions;
      actions.append(directions);

      article.append(actions);
      return article;
    }

    handleResultClick(event) {
      const button = event.target.closest('[data-show-dealer]');
      if (!button) return;
      const dealer = this.dealers.find((item) => item.id === button.dataset.showDealer);
      if (dealer) this.showDealerOnMap(dealer);
    }

    showDealerOnMap(dealer) {
      this.selectedDealerId = dealer.id;
      this.mapFrame.src = `https://www.google.com/maps?q=${dealer.latitude},${dealer.longitude}&z=14&output=embed`;
      this.mapFrame.title = dealer.name;
      this.mapFrame.style.pointerEvents = 'none';
      this.mapFrame.tabIndex = -1;
      this.mapTitle.textContent = dealer.name;
      this.resetMapButton.hidden = false;
      this.render();
      this.setMobileView('map');
    }

    resetMap() {
      this.selectedDealerId = null;
      this.mapFrame.src = this.mapFrame.dataset.allMapSrc;
      this.mapFrame.title = this.root.dataset.allDealersMap;
      this.mapFrame.style.removeProperty('pointer-events');
      this.mapFrame.removeAttribute('tabindex');
      this.mapTitle.textContent = this.root.dataset.allDealersMap;
      this.resetMapButton.hidden = true;
      this.render();
    }

    setMobileView(view) {
      if (!['list', 'map'].includes(view)) return;
      this.root.dataset.mobileView = view;
      this.viewButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.dealerView === view));
      });
      if (view === 'map' && window.matchMedia('(max-width: 749px)').matches) {
        this.root.querySelector('[data-dealer-map-panel]').scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }

    findNearestDealers() {
      if (!navigator.geolocation) {
        this.live.textContent = this.root.dataset.locationError;
        return;
      }

      const label = this.locationButton.querySelector('span');
      this.locationButton.disabled = true;
      label.textContent = this.root.dataset.locating;

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.userLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          this.search.value = '';
          this.country.value = '';
          this.locationButton.disabled = false;
          label.textContent = this.root.dataset.useLocation;
          if (this.selectedDealerId) {
            this.resetMap();
          } else {
            this.render();
          }
          this.setMobileView('list');
        },
        () => {
          this.locationButton.disabled = false;
          label.textContent = this.root.dataset.useLocation;
          this.live.textContent = this.root.dataset.locationError;
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }

    distanceInKilometres(origin, dealer) {
      const earthRadius = 6371;
      const latitudeDelta = this.toRadians(dealer.latitude - origin.latitude);
      const longitudeDelta = this.toRadians(dealer.longitude - origin.longitude);
      const originLatitude = this.toRadians(origin.latitude);
      const dealerLatitude = this.toRadians(dealer.latitude);
      const haversine =
        Math.sin(latitudeDelta / 2) ** 2 +
        Math.cos(originLatitude) * Math.cos(dealerLatitude) * Math.sin(longitudeDelta / 2) ** 2;
      return earthRadius * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
    }

    toRadians(value) {
      return (value * Math.PI) / 180;
    }

    formatDistance(distance) {
      if (distance < 10) return `${distance.toFixed(1)} km`;
      return `${Math.round(distance)} km`;
    }
  }

  const initialise = (scope = document) => {
    scope.querySelectorAll(SELECTORS.root).forEach((root) => {
      if (root.dataset.dealerLocatorInitialised === 'true') return;
      root.dataset.dealerLocatorInitialised = 'true';
      new MaybachDealerLocator(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
